import type {Options as VectorSourceOptions} from 'ol/source/Vector';
import type {Options as VectorLayerOptions} from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector.js';
import GeoJSON from 'ol/format/GeoJSON.js';
import Style from 'ol/style/Style.js';
import Stroke from 'ol/style/Stroke.js';
import Fill from 'ol/style/Fill.js';
import {getVectorContext} from 'ol/render.js';
import VectorLayer from 'ol/layer/Vector.js';
import {Z_INDEXES} from '../constants.ts';
import type Feature from 'ol/Feature';
import type {Geometry, MultiPolygon, Polygon} from 'ol/geom';
import {BASE_API_URL} from '../../envs.ts';
import type VectorContext from 'ol/render/VectorContext.js';

const SWISSPARK_FILL_RESOLUTION = 35;

const INNER_STROKE_FILL = new Style({
  stroke: new Stroke({
    color: 'rgba(0, 159, 128, 0.5)',
    width: 40,
  }),
});

const BORDER_STROKE = new Stroke({
  color: 'rgba(0, 159, 128, 1)',
  width: 3,
});

const FILLED_POLYGON_STYLE = [
  new Style({
    stroke: BORDER_STROKE,
    fill: new Fill({
      color: 'rgba(0, 159, 128, 0.5)',
    }),
  }),
];

const ONLY_BORDER_STYLE = [
  new Style({
    stroke: BORDER_STROKE,
  }),
];

export class SwissparkSource extends VectorSource {
  constructor(options?: VectorSourceOptions<Feature>) {
    super(
      Object.assign(
        {},
        {
          url: `${BASE_API_URL}/api/4/swissparks.geojson`,
          format: new GeoJSON(),
        },
        options,
      ),
    );
  }
}

export class SwissparkLayer extends VectorLayer<VectorSource> {
  constructor(options?: VectorLayerOptions) {
    super(
      Object.assign(
        {},
        {
          visible: false,
          source: new SwissparkSource(),
          zIndex: Z_INDEXES.ROUTE,
          style: (feature: Feature, resolution: number) =>
            resolution > SWISSPARK_FILL_RESOLUTION
              ? FILLED_POLYGON_STYLE
              : ONLY_BORDER_STYLE,
          properties: {name: 'swisspark'},
        },
        options,
      ),
    );

    this.on('postrender', (event) => {
      if (event.frameState.viewState.resolution > SWISSPARK_FILL_RESOLUTION)
        return;
      this.getSource().forEachFeature((feature) => {
        const vectorContext: any = getVectorContext(event);
        const geometry: Geometry = feature.getGeometry();
        setClippingGeometry(vectorContext, <Polygon>geometry);
        vectorContext.setStyle(INNER_STROKE_FILL);
        vectorContext.drawGeometry(geometry);
        vectorContext.context_.restore();
        vectorContext.contextStrokeState_ = null;
        vectorContext.contextFillState_ = null;
      });
    });
  }
}

function setClippingGeometry(
  vectorContext: VectorContext,
  geometry: Polygon | MultiPolygon,
) {
  // @ts-ignore
  const context = vectorContext.context_;
  const type = geometry.getType();
  const flatCoordinates = geometry.getOrientedFlatCoordinates();
  const stride = geometry.getStride();

  context.save();
  context.beginPath();
  if (type === 'Polygon') {
    const ends = (<Polygon>geometry).getEnds();
    for (let i = 0, ii = ends.length; i < ii; ++i) {
      // @ts-ignore
      vectorContext.moveToLineTo_(flatCoordinates, 0, ends[i], stride, true);
    }
  } else if (type === 'MultiPolygon') {
    let offset = 0;
    const endss = (<MultiPolygon>geometry).getEndss();
    for (let i = 0, ii = endss.length; i < ii; ++i) {
      const ends = endss[i];
      // @ts-ignore
      offset = vectorContext.drawRings_(flatCoordinates, offset, ends, stride);
    }
  } else {
    console.error('Unsupported geometry type', type);
  }
  context.clip();
}

export function resetClipping(vectorContext: VectorContext) {
  // @ts-ignore
  vectorContext.context_.restore();
  // the save/restore mess with fill and stroke state.
  // @ts-ignore
  vectorContext.contextStrokeState_ = null;
  // @ts-ignore
  vectorContext.contextFillState_ = null;
}
